import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'; // For the file input button
import ReceiptTypeSwitch from './ReceiptTypeSwitch';

function ImageUpload({ onUploadSuccess, onUploadError, onProcessing }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [receiptType, setReceiptType] = useState('grocery');
  // Local loading state for the button, if App.js's isProcessing is not directly disabling this button
  const [isUploading, setIsUploading] = useState(false);


  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setPreview(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault(); // Still useful if wrapped in a form, though not strictly necessary with MUI Button onClick
    if (!selectedFile) {
      onUploadError('Please select a file first.');
      return;
    }

    onProcessing(); // Notify App component that processing has started globally
    setIsUploading(true); // Local state for button visual

    const formData = new FormData();
    formData.append('receiptImage', selectedFile);
    formData.append('receipt_type', receiptType);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';

    try {
      const response = await fetch(`${backendUrl}/upload-receipt`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        // Try to parse error response from backend
        let errorDetail = `HTTP error! status: ${response.status}`;
        try {
            const errorData = await response.json();
            errorDetail = errorData.detail || errorData.message || errorDetail;
        } catch (e) {
            // If response is not JSON or empty
            const textError = await response.text();
            if (textError) errorDetail = textError;
        }
        throw new Error(errorDetail);
      }

      const data = await response.json();
      onUploadSuccess(data);
    } catch (error) {
      console.error('Upload failed:', error);
      onUploadError(error.message || 'Failed to upload and process receipt.');
    } finally {
      setIsUploading(false);
      // Reset file input after submission attempt if desired, or let user re-submit same file
      // setSelectedFile(null);
      // setPreview(null);
      // if (event.target.receiptUpload) event.target.receiptUpload.value = ""; // Clear file input
    }
  };

  return (
    <Box
      component="form" // Keep it as a form for semantics and onSubmit, or handle via Button's onClick directly
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2, // Spacing between elements
        p: 2,   // Padding around the component
        // border: '1px dashed grey', // Optional: for visual separation
        // borderRadius: 1
      }}
    >
      <ReceiptTypeSwitch
        value={receiptType}
        onChange={setReceiptType}
        disabled={isUploading}
      />

      <Button
        variant="outlined"
        component="label" // Allows the button to act as a label for a hidden file input
        startIcon={<PhotoCameraIcon />}
        fullWidth
      >
        Select Receipt Image
        <input
          type="file"
          id="receiptUpload" // Keep id if label association is needed elsewhere, though MUI handles it
          hidden // MUI way to hide the default file input
          accept="image/*"
          capture="environment" // Enables camera access, preferring back camera for document scanning
          onChange={handleFileChange}
        />
      </Button>

      {preview && (
        <Card sx={{ maxWidth: 345, mt: 1, width: '100%' }}>
          <CardMedia
            component="img"
            height="200" // Adjust as needed
            image={preview}
            alt="Receipt preview"
            sx={{ objectFit: 'contain', borderBottom: '1px solid #eee' }}
          />
          <CardContent sx={{textAlign: 'center'}}>
            <Typography variant="caption" color="text.secondary">
              {selectedFile ? selectedFile.name : 'Preview'}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Button
        type="submit" // Important if using Box as a form
        variant="contained"
        color="primary"
        startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
        disabled={!selectedFile || isUploading}
        fullWidth
        sx={{ mt: 1 }}
      >
        {isUploading ? 'Processing...' : 'Upload and Process'}
      </Button>
    </Box>
  );
}

export default ImageUpload;
